import { apiBaseUrl } from "./api";
import { sessionExpired } from "./session";

// Comme apiService.fetch, mais l'erreur garde son statut HTTP et son détail :
// l'éditeur doit distinguer un conflit de version (409) d'une autre erreur.
export class OfferApiError extends Error {
  constructor(status, detail) {
    const message = typeof detail === "string" ? detail : detail?.message || "Une erreur est survenue";
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

const request = async (endpoint, { method = "GET", body } = {}) => {
  const token = localStorage.getItem("auth_token");
  const response = await fetch(`${apiBaseUrl}${endpoint}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    sessionExpired();
    throw new OfferApiError(401, "Session expirée. Veuillez vous reconnecter.");
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    let detail = data?.detail ?? "Une erreur est survenue";
    if (Array.isArray(detail)) detail = detail.map((entry) => entry?.msg || JSON.stringify(entry)).join(" ; ");
    throw new OfferApiError(response.status, detail);
  }
  return data;
};

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

// PDF d'une proposition imprimé par le serveur : { blob, filename }. 503 : impression par le serveur indisponible.
const downloadPdf = async (id, revisionId) => {
  const token = localStorage.getItem("auth_token");
  const response = await fetch(`${apiBaseUrl}/offers/proposals/${id}/pdf${query({ revision_id: revisionId })}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (response.status === 401) {
    sessionExpired();
    throw new OfferApiError(401, "Session expirée. Veuillez vous reconnecter.");
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new OfferApiError(response.status, data?.detail ?? "Le PDF n'a pas pu être préparé.");
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  return { blob: await response.blob(), filename: encoded ? decodeURIComponent(encoded[1]) : "Proposition.pdf" };
};

export const offerApi = {
  permissions: () => request("/offers/permissions"),
  affaires: (params) => request(`/offers/affaires${query(params)}`),
  projectsWithoutAffaire: (params) => request(`/offers/projects-without-affaire${query(params)}`),
  openAffaire: (body) => request("/offers/affaires", { method: "POST", body }),
  list: (params) => request(`/offers/estimates${query(params)}`),
  get: (id, revisionId) => request(`/offers/estimates/${id}${query({ revision_id: revisionId })}`),
  create: (body) => request("/offers/estimates", { method: "POST", body }),
  save: (id, body) => request(`/offers/estimates/${id}`, { method: "PUT", body }),
  updateMeta: (id, body) => request(`/offers/estimates/${id}`, { method: "PATCH", body }),
  syncAffaire: (id, body) => request(`/offers/estimates/${id}/sync-affaire`, { method: "POST", body }),
  duplicate: (id, body = {}) => request(`/offers/estimates/${id}/duplicate`, { method: "POST", body }),
  revisions: (id) => request(`/offers/estimates/${id}/revisions`),
  restore: (id, revisionId, baseToken) =>
    request(`/offers/estimates/${id}/revisions/${revisionId}/restore`, { method: "POST", body: { base_token: baseToken } }),
  label: (id, revisionId, label) =>
    request(`/offers/estimates/${id}/revisions/${revisionId}/label`, { method: "PUT", body: { label } }),
  proposals: (params) => request(`/offers/proposals${query(params)}`),
  proposal: (id, revisionId) => request(`/offers/proposals/${id}${query({ revision_id: revisionId })}`),
  createProposal: (body) => request("/offers/proposals", { method: "POST", body }),
  saveProposal: (id, body) => request(`/offers/proposals/${id}`, { method: "PUT", body }),
  updateProposal: (id, body) => request(`/offers/proposals/${id}`, { method: "PATCH", body }),
  proposalRevisions: (id) => request(`/offers/proposals/${id}/revisions`),
  restoreProposal: (id, revisionId, baseToken) =>
    request(`/offers/proposals/${id}/revisions/${revisionId}/restore`, { method: "POST", body: { base_token: baseToken } }),
  labelProposal: (id, revisionId, label) =>
    request(`/offers/proposals/${id}/revisions/${revisionId}/label`, { method: "PUT", body: { label } }),
  proposalPdf: downloadPdf,
  saveClientTemplate: (id, clear = false) =>
    request(`/offers/proposals/${id}/client-template`, { method: "POST", body: { clear } }),
  comments: (proposalId, status) => request(`/offers/proposals/${proposalId}/comments${query({ status })}`),
  addComment: (proposalId, body) => request(`/offers/proposals/${proposalId}/comments`, { method: "POST", body }),
  resolveComment: (commentId, reply) => request(`/offers/comments/${commentId}/resolve`, { method: "POST", body: { reply } }),
  reopenComment: (commentId) => request(`/offers/comments/${commentId}/reopen`, { method: "POST" }),
  deleteComment: (commentId) => request(`/offers/comments/${commentId}`, { method: "DELETE" }),
  uploadAsset: (body) => request("/offers/assets", { method: "POST", body }),
  // Fond pro ou retouche décrite : une nouvelle image ({ ref }), l'originale reste.
  retouchAsset: (assetId, body) => request(`/offers/assets/${assetId}/retouch`, { method: "POST", body }),
  // Les images passent par l'API authentifiée : on les lit en blob, pas via <img src>.
  assetBlob: async (assetId) => {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(`${apiBaseUrl}/offers/assets/${assetId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new OfferApiError(response.status, "Image indisponible");
    return response.blob();
  },
  clientProfile: (clientId) => request(`/offers/client-profiles/${clientId}`),
  updateClientProfile: (clientId, body) => request(`/offers/client-profiles/${clientId}`, { method: "PUT", body }),
};
